import {
  Card,
  CodeBlock,
  Divider,
  HStack,
  Heading,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';
import { usePageEvent } from '../analytics/usePageEvent';

const PY_EXAMPLE = `import os, requests

resp = requests.post(
    "https://api.vectime.cloud/v1/forecast",
    headers={"Authorization": f"Bearer {os.environ['VECTIME_API_KEY']}"},
    json={
        # A single series, or a list of series to batch them in one call.
        "series": [[412, 430, 455, 471, 502, 538, 561, 590]],
        "horizon": 12,
        "quantiles": True,
    },
    timeout=120,
)
resp.raise_for_status()
out = resp.json()

median = out["forecasts"][0]["point"]
p10 = out["forecasts"][0]["quantiles"]["q10"]
p90 = out["forecasts"][0]["quantiles"]["q90"]`;

const TS_EXAMPLE = `const res = await fetch("https://api.vectime.cloud/v1/forecast", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.VECTIME_API_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    series: [[412, 430, 455, 471, 502, 538, 561, 590]],
    horizon: 12,
    quantiles: true,
  }),
});

if (res.status === 429) {
  // Rate limited: honour the server's own backoff hint.
  const wait = Number(res.headers.get("Retry-After") ?? 1);
  await new Promise((r) => setTimeout(r, wait * 1000));
}

const { forecasts } = await res.json();`;

const ERROR_EXAMPLE = `{
  "error": {
    "code": "horizon_limit_exceeded",
    "message": "horizon 500 exceeds your plan's maximum of 128",
    "details": { "max_horizon": 128, "plan": "starter" }
  }
}`;

interface Row {
  name: string;
  type: string;
  required: boolean;
  description: string;
}

const REQUEST_FIELDS: Row[] = [
  {
    name: 'series',
    type: 'number[] | number[][]',
    required: true,
    description:
      'The historical observations, oldest first, evenly spaced. Pass a flat array for one series or an array of arrays to batch. Each series needs at least 2 points; anything longer than your plan\'s context window is rejected rather than silently truncated.',
  },
  {
    name: 'horizon',
    type: 'integer',
    required: true,
    description: 'How many future steps to predict. Capped by your plan\'s max horizon.',
  },
  {
    name: 'quantiles',
    type: 'boolean',
    required: false,
    description:
      'Return the nine decile quantiles alongside the median. Defaults to true on plans that include quantiles.',
  },
];

const ERROR_CODES: Array<{ status: string; code: string; meaning: string }> = [
  { status: '401', code: 'missing_api_key / invalid_api_key / revoked_api_key', meaning: 'The key is absent, unknown, or revoked. Not retryable.' },
  { status: '402', code: 'quota_exceeded', meaning: 'The monthly call quota is spent. Upgrade, or wait for the UTC month to roll over.' },
  { status: '403', code: 'account_suspended', meaning: 'The account is suspended. Contact support.' },
  { status: '422', code: 'invalid_request', meaning: 'The payload is malformed — a non-finite value, an empty series, horizon < 1.' },
  { status: '422', code: 'horizon_limit_exceeded / context_limit_exceeded / series_limit_exceeded', meaning: 'The request is larger than your plan allows. `details` carries the actual ceiling.' },
  { status: '429', code: 'rate_limit_exceeded', meaning: 'Too many requests per minute. Retry after the seconds given in Retry-After.' },
  { status: '503', code: 'model_unavailable', meaning: 'The model is still warming up. Retryable within a few seconds.' },
  { status: '502', code: 'inference_failed', meaning: 'The forecasting backend failed. Retry with backoff; the call is not billed.' },
];

const SECTIONS: Array<{ id: string; label: string }> = [
  { id: 'authentication', label: 'Authentication' },
  { id: 'forecast', label: 'POST /v1/forecast' },
  { id: 'examples', label: 'Examples' },
  { id: 'models', label: 'GET /v1/models' },
  { id: 'rate-limits', label: 'Rate limits & quota' },
  { id: 'errors', label: 'Errors' },
];

export function DocsPage() {
  usePageEvent('docs_viewed');
  return (
    <div className="shell section-block">
      <div className="docs">
        <nav className="docs__nav" aria-label="On this page">
          <span className="docs__nav-label">Reference</span>
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="docs__link">
              {s.label}
            </a>
          ))}
        </nav>
      <VStack gap={5} className="docs__body">
        <VStack gap={1.5}>
          <span className="eyebrow">API reference</span>
          <Heading level={1} className="display--md">
            One endpoint. <span className="gradient-text">Nine quantiles.</span>
          </Heading>
          <p className="lede measure--wide">
            Authenticate with an API key, send observations, receive a median forecast and quantile
            bands. Two endpoints, one auth header, one error envelope.
          </p>
        </VStack>

        <Card padding={2} variant="muted">
          <Text size="sm" color="secondary">
            Working in n8n? The <a href="/integrations/n8n">VecTime node</a> does all of this for you
            — rows in, forecast rows out, no HTTP node to wire up.
          </Text>
        </Card>

        <VStack gap={2}>
          <Heading level={2} id="authentication">Authentication</Heading>
          <Text color="secondary">
            Send your key as a bearer token. <span className="mono">X-API-Key</span> is accepted as an
            alternative for clients that reserve the Authorization header.
          </Text>
          <CodeBlock
            code={'Authorization: Bearer vct_live_xxxxxxxxxxxxxxxxxxxxxxxx\n# or\nX-API-Key: vct_live_xxxxxxxxxxxxxxxxxxxxxxxx'}
            language="http"
            hasCopyButton
            container="card"
          />
          <Card padding={2} variant="muted">
            <Text size="sm" color="secondary">
              Keys are stored as a peppered HMAC — we cannot recover a lost key, only issue a new one.
              Treat <span className="mono">vct_live_*</span> keys as production credentials and keep
              them server-side.
            </Text>
          </Card>
        </VStack>

        <Divider />

        <VStack gap={2}>
          <HStack gap={1.5} vAlign="center">
            <span className="method">POST</span>
            <Heading level={2} id="forecast">/v1/forecast</Heading>
          </HStack>
          <Text color="secondary">Forecast one or many series in a single model pass.</Text>

          <Table density="balanced" dividers="rows">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Field</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Required</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {REQUEST_FIELDS.map((f) => (
                <TableRow key={f.name}>
                  <TableCell>
                    <span className="mono">{f.name}</span>
                  </TableCell>
                  <TableCell>
                    <Text size="sm" color="secondary">
                      <span className="mono">{f.type}</span>
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text size="sm">{f.required ? 'Yes' : 'No'}</Text>
                  </TableCell>
                  <TableCell>
                    <Text size="sm" color="secondary">
                      {f.description}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Text size="sm" color="secondary">
            Response quantile keys are <span className="mono">q10</span> …{' '}
            <span className="mono">q90</span> in steps of 10, where <span className="mono">q50</span>{' '}
            equals <span className="mono">point</span>. <span className="mono">mean</span> is the
            distribution mean, which differs from the median on skewed series.
          </Text>
        </VStack>

        <VStack gap={2}>
          <Heading level={2} id="examples">Examples</Heading>
          <div className="split">
            <VStack gap={1.5}>
              <span className="code-label">
                <span className="code-label__dot" />
                Python
              </span>
              <div className="code-frame">
                <CodeBlock code={PY_EXAMPLE} language="python" hasCopyButton container="card" />
              </div>
            </VStack>
            <VStack gap={1.5}>
              <span className="code-label">
                <span className="code-label__dot code-label__dot--warm" />
                TypeScript
              </span>
              <div className="code-frame">
                <CodeBlock code={TS_EXAMPLE} language="typescript" hasCopyButton container="card" />
              </div>
            </VStack>
          </div>
        </VStack>

        <Divider />

        <VStack gap={2}>
          <HStack gap={1.5} vAlign="center">
            <span className="method method--get">GET</span>
            <Heading level={2} id="models">/v1/models</Heading>
          </HStack>
          <Text color="secondary">
            Reports the active model plus the ceilings your plan applies, so a client can discover
            limits instead of hard-coding them.
          </Text>
        </VStack>

        <Divider />

        <VStack gap={2}>
          <Heading level={2} id="rate-limits">Rate limits and quota</Heading>
          <Text color="secondary">
            Limits are enforced per API key with a token bucket: you may spike up to your plan&apos;s
            burst size, then settle at the sustained per-minute rate. Every response carries the
            current state.
          </Text>
          <CodeBlock
            code={`RateLimit-Limit: 60          # requests per minute on this plan
RateLimit-Remaining: 41      # tokens left in the bucket right now
RateLimit-Reset: 19          # seconds until the bucket is full again
X-Quota-Limit: 50000         # monthly call allowance
X-Quota-Used: 12043          # calls consumed this UTC month
Retry-After: 2               # only on 429`}
            language="http"
            hasCopyButton
            container="card"
          />
        </VStack>

        <VStack gap={2}>
          <Heading level={2} id="errors">Errors</Heading>
          <Text color="secondary">Every failure uses the same envelope.</Text>
          <CodeBlock code={ERROR_EXAMPLE} language="json" hasCopyButton container="card" />

          <Table density="balanced" dividers="rows">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Code</TableHeaderCell>
                <TableHeaderCell>Meaning</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ERROR_CODES.map((e) => (
                <TableRow key={e.code}>
                  <TableCell>
                    <span className="mono">{e.status}</span>
                  </TableCell>
                  <TableCell>
                    <span className="mono">{e.code}</span>
                  </TableCell>
                  <TableCell>
                    <Text size="sm" color="secondary">
                      {e.meaning}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </VStack>
      </VStack>
      </div>
    </div>
  );
}
